// src/modules/brands/hooks/useBrandData.ts
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { BrandService } from "../services/brand.services";
import {
  BrandDto,
  BrandHighlightDto,
  UserInfo,
  BrandMember,
} from "../models/brand.model";
import {
  sortByOrderIndex,
  reindexItems,
} from "@modules/shared/utils/state-updates";

const normalizeHighlights = (items: BrandHighlightDto[]) => {
  const unique = new Map<string, BrandHighlightDto>();
  items.forEach((item) => {
    if (!item.b2b_id) return;
    const existing = unique.get(item.b2b_id);
    if (!existing || item.order_index < existing.order_index)
      unique.set(item.b2b_id, item);
  });
  return reindexItems(sortByOrderIndex(Array.from(unique.values())));
};

export const useBrandData = () => {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const location = useLocation();

  const service = useMemo(() => new BrandService(), []);

  // State cho danh sách brands (dùng ở trang list)
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [highlights, setHighlights] = useState<BrandHighlightDto[]>([]);
  const [userMap, setUserMap] = useState<Map<string, UserInfo>>(new Map());
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State cho chi tiết brand (dùng ở trang detail)
  const [currentBrand, setCurrentBrand] = useState<BrandDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoaded, setDetailLoaded] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadingRef = useRef(false);
  const initialLoadDone = useRef(false);
  const userMapRef = useRef(userMap);
  userMapRef.current = userMap;

  const isBrandListRoute = location.pathname === "/b2b/brands";
  const isBrandDetailRoute =
    location.pathname.startsWith("/b2b/brands/") &&
    location.pathname !== "/b2b/brands";

  const authArgs = useMemo(
    () => ({ auth, setAuth, logout }),
    [auth, setAuth, logout],
  );

  // Hàm load thông tin một user (cache)
  const loadUserInfo = useCallback(
    async (userId: string) => {
      if (!userId) return null;
      const cached = userMapRef.current.get(userId);
      if (cached) return cached;
      const user = await service
        .getUserById(userId, authArgs)
        .catch(() => null);
      if (user) {
        setUserMap((prev) => {
          if (prev.has(userId)) return prev;
          return new Map(prev).set(userId, user);
        });
      }
      return user;
    },
    [authArgs, service],
  );

  const loadBatchUserInfo = useCallback(
    async (userIds: string[]) => {
      const missing = userIds.filter((id) => !userMapRef.current.has(id));
      if (missing.length === 0) return;
      await Promise.all(missing.map((id) => loadUserInfo(id)));
    },
    [loadUserInfo],
  );

  // ---- Load danh sách tất cả brands ----
  const loadAllBrands = useCallback(async () => {
    if (!auth || !isBrandListRoute) {
      if (!auth) setLoaded(true);
      return;
    }
    if (loadingRef.current || initialLoadDone.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const [allBrands, highlightsResult] = await Promise.all([
        service.getAllBrands(authArgs),
        service.getHighlights(authArgs).catch(() => []),
      ]);

      setBrands(allBrands);
      setHighlights(normalizeHighlights(highlightsResult));

      const userIds = new Set<string>();
      allBrands.forEach((b) => {
        if (b.owner_id) userIds.add(b.owner_id);
        b.members.forEach((m: BrandMember) => userIds.add(m.user_id));
      });
      await loadBatchUserInfo(Array.from(userIds));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load brands");
    } finally {
      setLoading(false);
      setLoaded(true);
      loadingRef.current = false;
      initialLoadDone.current = true;
    }
  }, [auth, authArgs, isBrandListRoute, service, loadBatchUserInfo]);

  // ---- Load chi tiết một brand ----
  const loadBrandDetail = useCallback(
    async (brandId: string) => {
      if (!auth || !brandId) return;
      setDetailLoading(true);
      setDetailError(null);

      try {
        const brand = await service.getBrandById(brandId, authArgs);
        if (!brand) {
          setDetailError("Không tìm thấy thương hiệu");
          setCurrentBrand(null);
          return;
        }
        setCurrentBrand(brand);

        // Load thông tin owner và members
        const userIds = new Set<string>();
        if (brand.owner_id) userIds.add(brand.owner_id);
        brand.members.forEach((m: BrandMember) => userIds.add(m.user_id));
        await loadBatchUserInfo(Array.from(userIds));

        // Nếu chưa có list, ta có thể thêm brand này vào list (để dùng chung)
        setBrands((prev) => {
          const exists = prev.some((b) => b.id === brand.id);
          if (!exists) {
            return [...prev, brand];
          }
          return prev.map((b) => (b.id === brand.id ? brand : b));
        });

        setDetailLoaded(true);
      } catch (err) {
        setDetailError(
          err instanceof Error ? err.message : "Không thể tải chi tiết brand",
        );
        setCurrentBrand(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [auth, authArgs, service, loadBatchUserInfo],
  );

  // Effect: chỉ load all khi ở trang list
  useEffect(() => {
    if (!isBrandListRoute) {
      if (isBrandDetailRoute) {
        setLoaded(true);
      }
      return;
    }
    if (!auth) {
      setBrands([]);
      setHighlights([]);
      setUserMap(new Map());
      setLoaded(true);
      setError(null);
      initialLoadDone.current = false;
      loadingRef.current = false;
      return;
    }
    loadAllBrands();
  }, [auth, isBrandListRoute, isBrandDetailRoute, loadAllBrands]);

  const refresh = useCallback(async () => {
    initialLoadDone.current = false;
    await loadAllBrands();
  }, [loadAllBrands]);

  return {
    // Dùng cho list
    brands,
    setBrands,
    highlights,
    setHighlights,
    userMap,
    setUserMap,
    loading,
    loaded,
    error,
    refresh,

    // Dùng cho detail
    currentBrand,
    detailLoading,
    detailLoaded,
    detailError,
    loadBrandDetail,

    authArgs,
    service,
    loadUserInfo,
  };
};
